import { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import { useTenant } from '@core/tenant';
import type { SupportedLanguage } from '@core/i18n/types';
import { sanitizeHtml } from '@shared/utils/sanitize';
import BookingHeader from '@features/booking/components/BookingHeader';
import { BookingChrome } from '@features/booking/layouts';

const SUPPORTED_LANGS: readonly SupportedLanguage[] = ['en', 'es', 'fr', 'de', 'pt'];
const TOTAL_TERM_PARAGRAPHS = 10;

const isSupportedLang = (value: string | undefined): value is SupportedLanguage =>
  !!value && (SUPPORTED_LANGS as readonly string[]).includes(value);

const TermsAndConditionsPage = () => {
  const { lang } = useParams<{ lang?: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('booking');
  const { restaurant } = useTenant();

  useEffect(() => {
    if (!isSupportedLang(lang) || i18n.language === lang) return;
    const previous = i18n.language;
    i18n.changeLanguage(lang);
    return () => {
      if (i18n.language !== previous) i18n.changeLanguage(previous);
    };
  }, [lang, i18n]);

  const activeLang = isSupportedLang(lang) ? lang : i18n.language;

  const { title, paragraphs } = useMemo(() => {
    const appTexts = restaurant?.appTexts?.[activeLang];
    const titleText = appTexts?.MODAL_TERMS_TITLE;
    const items: string[] = [];
    if (appTexts) {
      for (let i = 1; i <= TOTAL_TERM_PARAGRAPHS; i++) {
        const text = appTexts[`MODAL_TERMS_TEXT_${i}`];
        if (text) items.push(text);
      }
    }
    return { title: titleText, paragraphs: items };
  }, [restaurant?.appTexts, activeLang]);

  const hasContent = paragraphs.length > 0;

  return (
    <BookingChrome flexCol>
        <BookingHeader />

        <main className="booking-content pt-8 pb-10 px-6 sm:px-10 flex-1">
          <h1
            className="text-3xl sm:text-4xl font-display text-foreground mb-8 text-center"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(title ?? t('STRING_TERMS')),
            }}
          />

          {hasContent && (
            <div className="space-y-4 text-foreground/80 text-[15px] leading-relaxed">
              {paragraphs.map((html, index) => (
                <div
                  key={index}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
                />
              ))}
            </div>
          )}
        </main>

        <footer className="border-t border-border">
          <div className="flex items-center px-6 py-4">
            <button
              aria-label={t('NEW_MODAL_BACK')}
              className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
              onClick={() => navigate('/')}
              type="button"
            >
              <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
            </button>
          </div>
        </footer>
    </BookingChrome>
  );
};

export default TermsAndConditionsPage;
